import { useCallback, useEffect, useRef, useState } from 'react'
import { SectionCard } from './common/SectionCard'
import { Icons } from './icons'
import { TemplateService, type TemplateStatus, type TemplateSummary } from '../services/TemplateService'
import { VcardService } from '../services/VcardService'
import { notifySuccess, notifyError, notifyWarning } from '../utils/notify'

function TemplateInfo({ tpl, label }: { tpl: TemplateSummary; label: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" style={{ color: 'var(--bmc-fg-3)' }}>
      <span className={`badge ${tpl.status === 'active' ? 'active' : 'inactive'}`}>{label}</span>
      <strong style={{ color: 'var(--bmc-fg)' }}>{tpl.name}</strong> v{tpl.version} · Kontrakt v{tpl.contractVersion}
      {tpl.createdAt && <span>· {new Date(tpl.createdAt).toLocaleString('de-DE')}</span>}
    </div>
  )
}

/** Brand-Template-Sektion im Brand-Editor: Status, ZIP-Upload, Aktivieren, Preview-Link (Task 12). */
export function BrandTemplateSection({ brandKey }: { brandKey: string }) {
  const [status, setStatus] = useState<TemplateStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [uploadErrors, setUploadErrors] = useState<string[]>([])
  const fileInput = useRef<HTMLInputElement>(null)

  const refresh = useCallback(async () => {
    try {
      setStatus(await TemplateService.getStatus(brandKey))
    } catch {
      notifyError('Template-Status konnte nicht geladen werden')
    }
  }, [brandKey])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const handleFile = async (file: File | undefined) => {
    if (!file) return
    setBusy(true)
    setUploadErrors([])
    try {
      await TemplateService.uploadDraft(brandKey, file)
      notifySuccess('Template als Entwurf hochgeladen')
      await refresh()
    } catch (error) {
      const data = (error as { response?: { data?: { error?: string; details?: string[] } } }).response?.data
      setUploadErrors(data?.details?.length ? data.details : [data?.error ?? 'Upload fehlgeschlagen'])
    } finally {
      setBusy(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  const handleActivate = async () => {
    setBusy(true)
    try {
      await TemplateService.activate(brandKey)
      notifySuccess('Template aktiviert')
      await refresh()
    } catch {
      notifyError('Aktivieren fehlgeschlagen')
    } finally {
      setBusy(false)
    }
  }

  const handlePreviewLink = async () => {
    setBusy(true)
    try {
      const [{ token }, vcards] = await Promise.all([TemplateService.previewToken(brandKey), VcardService.list(brandKey)])
      const urlKey = vcards[0]?.urlKey
      if (!urlKey) {
        notifyWarning('Keine vCard in dieser Brand — Preview-Link kann nicht gebaut werden')
        return
      }
      const link = `${window.location.origin}/${urlKey}?preview=${encodeURIComponent(token)}`
      await navigator.clipboard.writeText(link)
      notifySuccess('Preview-Link kopiert (7 Tage gültig)')
    } catch {
      notifyError('Preview-Link konnte nicht erstellt werden')
    } finally {
      setBusy(false)
    }
  }

  return (
    <SectionCard icon={Icons.card} title="vCard-Template">
      <div className="flex flex-col gap-4">
        {status?.active ? (
          <TemplateInfo tpl={status.active} label="Aktiv" />
        ) : (
          <div className="fhint">Globaler Default (classic) — kein Brand-Template aktiv.</div>
        )}

        {status?.draft && (
          <div className="flex flex-col gap-3">
            <TemplateInfo tpl={status.draft} label="Entwurf" />
            <div className="flex flex-wrap gap-2">
              <button type="button" className="btn primary sm" disabled={busy} onClick={handleActivate}>
                Aktivieren
              </button>
              <button type="button" className="btn outline sm" disabled={busy} onClick={handlePreviewLink}>
                Preview-Link kopieren
              </button>
            </div>
          </div>
        )}

        <div>
          <button
            type="button"
            className="btn outline sm"
            disabled={busy}
            onClick={() => fileInput.current?.click()}
          >
            Template-ZIP hochladen{status?.draft ? ' (ersetzt Entwurf)' : ''}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept=".zip,application/zip"
            style={{ display: 'none' }}
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
        </div>

        {uploadErrors.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3.5 py-3 text-sm text-destructive">
            Template-Paket ungültig:
            <ul className="mt-1 list-disc pl-5">
              {uploadErrors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </SectionCard>
  )
}
